/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/scandipwa
 * @link https://github.com/scandipwa/scandipwa
 */

import CartQuery from 'Query/Cart.query';
import { CartDispatcher as SourceCartDispatcher } from 'SourceStore/Cart/Cart.dispatcher';
import { updateIsLoadingCart } from 'Store/Cart/Cart.action';
import { updateEmail, updateShippingFields } from 'Store/Checkout/Checkout.action';
import { showNotification } from 'Store/Notification/Notification.action';
import { getAuthorizationToken } from 'Util/Auth';
import { getCartId } from 'Util/Cart';
import { fetchMutation, fetchQuery, getErrorMessage } from 'Util/Request';

/** @namespace Bookland/Store/Cart/Dispatcher */
export class CartDispatcher extends SourceCartDispatcher {
    isVirtualAndBookstore(items, shippingAddresses) {
        let containeVirtual = false;
        let containsBookstore = false;

        items.forEach(({ product: { only_in_bookstore, type_id } }) => {
            if (type_id === 'virtual') {
                containeVirtual = true;
            }

            if (only_in_bookstore || shippingAddresses.some(({ selected_shipping_method }) => selected_shipping_method?.carrier_code === 'instore')) {
                containsBookstore = true;
            }
        });

        return containeVirtual && containsBookstore;
    }

    async updateInitialCartData(dispatch, isForCustomer = false, disableLoader = false) {
        // Need to get current cart from BE, update cart
        try {
            // ! Get quote token first (local or from the backend) just to make sure it exists

            if (!disableLoader) {
                dispatch(updateIsLoadingCart(true));
            }
            // ! Get quote token first (local or from the backend) just to make sure it exists
            const quoteId = await this._getCartId(dispatch);

            let cartDetails = await fetchQuery(
                CartQuery.getCartQuery(
                    quoteId
                )
            );

            if (this.isVirtualAndBookstore(cartDetails.cartData.items, cartDetails.cartData.shipping_addresses)) {
                await fetchMutation(CartQuery.getRemoveAddressFromCartMutation(quoteId));

                cartDetails = await fetchQuery(
                    CartQuery.getCartQuery(
                        quoteId
                    )
                );
            }

            const {
                cartData = {},
                cartData: {
                    is_virtual = false,
                    shipping_address: {
                        selected_shipping_method: {
                            address,
                            address: {
                                street = null,
                                email = ''
                            } = {}
                        } = {},
                        method_code
                    } = {}
                } = {}
            } = cartDetails;

            if (address && street) {
                if (!is_virtual) {
                    await dispatch(
                        updateShippingFields({
                            ...this.prepareCheckoutAddressFormat(address),
                            method_code
                        })
                    );
                }

                await dispatch(updateEmail(email));
            }

            if (isForCustomer && !getAuthorizationToken()) {
                dispatch(updateIsLoadingCart(false));

                return null;
            }

            await this._updateCartData(cartData, dispatch);

            if (!disableLoader) {
                dispatch(updateIsLoadingCart(false));
            }

            return null;
        } catch (error) {
            dispatch(updateIsLoadingCart(false));

            return this.createGuestEmptyCart(dispatch);
        }
    }

    async addProductToCart(dispatch, options = {}) {
        const { products = [], cartId: userCartId } = options;

        const cartId = userCartId || getCartId();

        if (!Array.isArray(products) || products.length === 0) {
            dispatch(showNotification('error', __('No product data!')));

            return Promise.reject();
        }

        try {
            if (!cartId) {
                return Promise.reject();
            }

            const { addProductsToCart: { user_errors: errors = [] } = {} } = await fetchMutation(
                CartQuery.getAddProductToCartMutation(cartId, products)
            );

            if (Array.isArray(errors) && errors.length > 0) {
                errors.forEach((error) => {
                    dispatch(showNotification('error', getErrorMessage(error)));
                });

                return Promise.reject();
            }
            dispatch({ type: 'ADDED_TO_CART', toast: __('Product was added to cart!') });
            await this.updateInitialCartData(dispatch);
            dispatch(showNotification('success', __('Product was added to cart!')));
        } catch (error) {
            if (!navigator.onLine) {
                dispatch(showNotification('error', __('Not possible to fetch while offline')));

                return Promise.reject();
            }

            dispatch(showNotification('error', getErrorMessage(error)));

            return Promise.reject();
        }

        return Promise.resolve();
    }

    async resetToast(dispatch) {
        dispatch({ type: 'ADDED_TO_CART', toast: null });

        return Promise.resolve();
    }
}

export default new CartDispatcher();
